'use client';

import {
  type ReactNode,
  type Ref,
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useMergeRefs } from '@floating-ui/react';
import classnames from 'classnames';

import type { SlideDownProps } from './SlideDown.types';

import styles from './SlideDown.module.scss';

export function SlideDown({
  as = 'div',
  children,
  className,
  closed = false,
  transitionOnAppear = true,
  style,
  testId = 'ui-slide-down',
  ref,
  ...rest
}: SlideDownProps & { ref?: Ref<HTMLDivElement> }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<string | null>(null);
  const isMountedRef = useRef(false);
  const isTransitioningRef = useRef(false);
  const [displayedChildren, setDisplayedChildren] =
    useState<ReactNode>(children);
  const [childrenLeaving, setChildrenLeaving] = useState(false);

  // Merge refs: forward the ref to the content element
  const mergedRef = useMergeRefs([ref, contentRef]);

  // Track children changes for exit animation
  useEffect(() => {
    if (children) {
      setDisplayedChildren(children);
      setChildrenLeaving(false);
    } else if (displayedChildren) {
      setChildrenLeaving(true);
    }
  }, [children, displayedChildren]);

  const startTransition = useCallback(
    (element: HTMLDivElement, prevHeight: string) => {
      const shouldBeOpen = !closed && !childrenLeaving && displayedChildren;
      let endHeight = '0px';

      // Remove closed class to allow transition (display: none prevents transitions)
      element.classList.remove(styles['main--closed']);

      if (shouldBeOpen) {
        // Opening: measure the natural height
        element.style.height = 'auto';
        endHeight = getComputedStyle(element).height;
      }

      // Only transition if height actually changed
      const heightChanged =
        parseFloat(endHeight).toFixed(2) !== parseFloat(prevHeight).toFixed(2);

      if (heightChanged) {
        isTransitioningRef.current = true;
        element.classList.add(styles['main--transitioning']);
        element.style.height = prevHeight;
        // Force repaint to ensure the previous height is applied
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        element.offsetHeight;
        element.style.transitionProperty = 'height';
        element.style.height = endHeight;
      } else {
        // No transition needed, set final state immediately
        if (shouldBeOpen) {
          element.style.height = 'auto';
          element.classList.remove(styles['main--closed']);
        } else {
          element.style.height = '0px';
          element.classList.add(styles['main--closed']);
        }
      }

      // Store current height for next update
      prevHeightRef.current = endHeight;
    },
    [closed, childrenLeaving, displayedChildren],
  );

  const endTransition = useCallback(() => {
    const element = contentRef.current;
    if (!element || !isTransitioningRef.current) return;

    isTransitioningRef.current = false;

    // Remove transitioning class and disable transitions
    element.classList.remove(styles['main--transitioning']);
    element.style.transitionProperty = 'none';

    const shouldBeOpen = !closed && !childrenLeaving && displayedChildren;

    if (shouldBeOpen) {
      // Opening: set height to auto and ensure closed class is removed
      element.classList.remove(styles['main--closed']);
      element.style.height = 'auto';
    } else {
      // Closing: immediately hide the element
      // We're already at 0px from the transition, so just add closed class
      element.style.height = '0px';
      element.classList.add(styles['main--closed']);
    }

    // Clear children after hiding (for exit animations)
    if (childrenLeaving) {
      setDisplayedChildren(null);
      setChildrenLeaving(false);
    }
  }, [closed, displayedChildren, childrenLeaving]);

  // Handle transition end - immediately hide when closing
  const handleTransitionEnd = useCallback(
    (evt: TransitionEvent) => {
      const element = contentRef.current;
      if (!element || evt.target !== element || evt.propertyName !== 'height') {
        return;
      }

      const isClosing = closed || !displayedChildren;

      if (isClosing) {
        // When closing, hide immediately in a single synchronous operation
        // Do everything in one go to prevent any visual flash
        element.style.transitionProperty = 'none';
        element.style.height = '0px';
        element.classList.remove(styles['main--transitioning']);
        element.classList.add(styles['main--closed']);
        isTransitioningRef.current = false;

        // Clear children after hiding (for exit animations)
        if (childrenLeaving) {
          setDisplayedChildren(null);
          setChildrenLeaving(false);
        }
      } else {
        // Opening: use the standard end transition logic
        endTransition();
      }
    },
    [closed, displayedChildren, childrenLeaving, endTransition],
  );

  // Initialize on mount
  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || isMountedRef.current) return;

    isMountedRef.current = true;

    if (closed || !displayedChildren) {
      element.classList.add(styles['main--closed']);
      element.style.height = '0px';
      prevHeightRef.current = '0px';
    } else if (transitionOnAppear) {
      // Start transition from 0px
      prevHeightRef.current = '0px';
      startTransition(element, '0px');
    } else {
      element.classList.remove(styles['main--closed']);
      element.style.height = 'auto';
      prevHeightRef.current = getComputedStyle(element).height;
    }
  }, [closed, displayedChildren, transitionOnAppear, startTransition]);

  // Handle updates after mount
  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || !isMountedRef.current) return;

    // Get snapshot of current height before update. While a transition is in
    // flight the cached height is its *target*, not what is on screen: a
    // re-render mid-animation (a parent handing down fresh children on every
    // render) has to resume from the live height, or it measures no change and
    // snaps straight to the target, killing the animation.
    const snapshot = isTransitioningRef.current
      ? element.getBoundingClientRect().height + 'px'
      : prevHeightRef.current || element.getBoundingClientRect().height + 'px';

    startTransition(element, snapshot);
  }, [closed, displayedChildren, childrenLeaving, startTransition]);

  const containerClassName = classnames(styles['main'], className);

  return (
    <div style={style} className={className} data-testid={testId}>
      {createElement(
        as,
        {
          ref: mergedRef,
          className: containerClassName,
          onTransitionEnd: handleTransitionEnd,
          'data-testid': `${testId}_content`,
          ...rest,
        },
        displayedChildren,
      )}
    </div>
  );
}
